import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Sidebar from "./Sidebar";
import "../styles/theme.css";
import "../styles/dashboard.css";
import "../styles/auth.css";
import { useSidebar } from "../App";
import {
  getApiUrl,
  getCsrfToken,
  checkAuth,
  readJobsApiTarget,
  writeJobsApiTarget,
} from "../utils/apiHelper";
import { validatePasswordStrength } from "../utils/passwordValidation";
import { RefreshCw, Copy, Check, Eye, EyeOff, Loader2, Pencil, X } from "lucide-react";

const iconBtnStyle = {
  padding: 0,
  margin: 0,
  width: "32px",
  height: "32px",
  minWidth: "32px",
  minHeight: "32px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  boxSizing: "border-box",
  flexShrink: 0,
  border: "none",
  borderRadius: "8px",
  background: "linear-gradient(var(--btn-gradient-start), var(--btn-gradient-end))",
  color: "var(--btn-text, #fff)",
  cursor: "pointer",
};

const fieldLabelStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "var(--muted-text)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const panelStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: 0,
  padding: "24px",
  height: "100%",
  boxSizing: "border-box",
};

const fieldRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  minHeight: "32px",
};

const valuePillStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "13px",
  color: "var(--card-text)",
  padding: "0 10px",
  height: "32px",
  borderRadius: "8px",
  backgroundColor: "var(--card-bg-alt, rgba(148,163,184,0.08))",
  boxSizing: "border-box",
};

const panelBodyStyle = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  justifyContent: "center",
};

const modalLabelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "600",
  color: "var(--card-text)",
  marginBottom: "6px",
  opacity: 0.8,
};

const modalInputStyle = {
  padding: "10px 12px",
  border: "2px solid var(--card-border)",
  borderRadius: "8px",
  backgroundColor: "var(--card-bg)",
  color: "var(--card-text)",
  fontSize: "13px",
  width: "100%",
  boxSizing: "border-box",
  height: "40px",
};

const languageSelectStyle = {
  padding: "6px 32px 6px 10px",
  border: "2px solid var(--card-border)",
  borderRadius: "8px",
  backgroundColor: "var(--card-bg)",
  color: "var(--card-text)",
  fontSize: "13px",
  minWidth: "120px",
  height: "32px",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
  backgroundSize: "12px",
  cursor: "pointer",
};

const MAX_FAILED_TESTS_MIN = 5000;
const MAX_FAILED_TESTS_MAX = 20000;
const MAX_FAILED_TESTS_DEFAULT = 5000;
const SETTINGS_API = '/api/internal/user/settings/';

const clampMaxFailedTests = (value) => {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return MAX_FAILED_TESTS_DEFAULT;
  return Math.min(MAX_FAILED_TESTS_MAX, Math.max(MAX_FAILED_TESTS_MIN, n));
};

const Account = () => {
  const { sidebarCollapsed } = useSidebar();
  const { t, i18n } = useTranslation("account");
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(true);
  const [twoFaSaving, setTwoFaSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // confirmAction: 'api-key' | 'reveal-api-key' | '2fa' | null
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [confirmPasswordLoading, setConfirmPasswordLoading] = useState(false);
  const [pendingTwoFaEnabled, setPendingTwoFaEnabled] = useState(null);
  const [pendingRevealIntent, setPendingRevealIntent] = useState(null); // 'view' | 'copy'
  const [apiKey, setApiKey] = useState(null);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(true);
  const [apiKeyCreating, setApiKeyCreating] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKeyCopyDone, setApiKeyCopyDone] = useState(false);
  const [maxFailedTests, setMaxFailedTests] = useState(String(MAX_FAILED_TESTS_DEFAULT));
  const [maxFailedTestsSaving, setMaxFailedTestsSaving] = useState(false);
  const [maxFailedTestsMessage, setMaxFailedTestsMessage] = useState('');
  const [maxFailedTestsError, setMaxFailedTestsError] = useState('');
  const [jobsApiTarget, setJobsApiTarget] = useState(readJobsApiTarget);

  const loadApiKey = useCallback(async () => {
    setApiKeyLoading(true);
    try {
      const keyResp = await fetch(getApiUrl('/api/internal/api-key/read/'), {
        method: 'GET',
        credentials: 'include',
      });
      const keyData = await keyResp.json().catch(() => ({}));
      setHasApiKey(Boolean(keyResp.ok && keyData.has_key));
      setApiKey(null);
      setApiKeyVisible(false);
    } catch (keyErr) {
      console.error('Error fetching API key:', keyErr);
      setHasApiKey(false);
      setApiKey(null);
      setApiKeyVisible(false);
    } finally {
      setApiKeyLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    setTwoFaLoading(true);
    try {
      const resp = await fetch(getApiUrl(SETTINGS_API), {
        method: 'GET',
        credentials: 'include',
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success !== false) {
        if (typeof data.is_2fa_enabled === 'boolean') {
          setTwoFaEnabled(data.is_2fa_enabled);
        }
        if (data.maximum_failed_tests != null) {
          setMaxFailedTests(String(clampMaxFailedTests(data.maximum_failed_tests)));
        }
      }
    } catch (err) {
      console.error('Error fetching account settings:', err);
    } finally {
      setTwoFaLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchAccountPage = async () => {
      try {
        const authResult = await checkAuth();
        if (authResult.authenticated && authResult.user) {
          setUserData({
            id: authResult.user.id || '',
            email: authResult.user.email || ''
          });
        } else {
          setUserData({ id: '', email: '' });
        }

        await Promise.all([loadSettings(), loadApiKey()]);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUserData({ id: '', email: '' });
        setTwoFaLoading(false);
        setApiKeyLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAccountPage();
  }, [loadApiKey, loadSettings]);

  const postSettings = async (payload) => {
    const csrfToken = await getCsrfToken();
    const resp = await fetch(getApiUrl(SETTINGS_API), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, data };
  };

  const resetPasswordForm = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const openPasswordModal = () => {
    resetPasswordForm();
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    if (passwordLoading) return;
    setShowPasswordModal(false);
    resetPasswordForm();
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordError('');
    setPasswordSuccess('');
  };

  const validatePasswordForm = () => {
    if (!passwordForm.currentPassword) {
      setPasswordError(t("password.currentRequired"));
      return false;
    }
    const strengthError = validatePasswordStrength(passwordForm.newPassword);
    if (strengthError) {
      setPasswordError(t("password.strength"));
      return false;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t("password.mismatch"));
      return false;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError(t("password.mustDiffer"));
      return false;
    }
    return true;
  };

  const clearConfirmPasswordModal = () => {
    setConfirmAction(null);
    setConfirmPassword('');
    setConfirmPasswordError('');
    setPendingTwoFaEnabled(null);
    setPendingRevealIntent(null);
  };

  const closeConfirmPasswordModal = () => {
    if (confirmPasswordLoading) return;
    clearConfirmPasswordModal();
  };

  const openConfirmPasswordModal = (action, intent = null) => {
    setConfirmAction(action);
    setConfirmPassword('');
    setConfirmPasswordError('');
    setPendingTwoFaEnabled(action === '2fa' ? !twoFaEnabled : null);
    setPendingRevealIntent(action === 'reveal-api-key' ? intent : null);
  };

  const confirmModalCopy = (() => {
    if (confirmAction === 'api-key') {
      return {
        title: t("confirmModal.resetApiKey.title"),
        description: t("confirmModal.resetApiKey.description"),
        submitLabel: t("confirmModal.resetApiKey.submit"),
        submittingLabel: t("confirmModal.resetApiKey.submitting"),
      };
    }
    if (confirmAction === 'reveal-api-key') {
      const copying = pendingRevealIntent === 'copy';
      return {
        title: copying
          ? t("confirmModal.revealApiKey.titleCopy")
          : t("confirmModal.revealApiKey.titleView"),
        description: t("confirmModal.revealApiKey.description"),
        submitLabel: copying
          ? t("confirmModal.revealApiKey.submitCopy")
          : t("confirmModal.revealApiKey.submitView"),
        submittingLabel: t("confirmModal.revealApiKey.submitting"),
      };
    }
    if (confirmAction === '2fa') {
      const enabling = pendingTwoFaEnabled === true;
      return {
        title: enabling
          ? t("confirmModal.twoFactor.titleEnable")
          : t("confirmModal.twoFactor.titleDisable"),
        description: t("confirmModal.twoFactor.description"),
        submitLabel: enabling
          ? t("confirmModal.twoFactor.submitEnable")
          : t("confirmModal.twoFactor.submitDisable"),
        submittingLabel: t("confirmModal.twoFactor.submitting"),
      };
    }
    return {
      title: '',
      description: '',
      submitLabel: t("confirm"),
      submittingLabel: t("savingEllipsis"),
    };
  })();

  const handleConfirmPasswordSubmit = async (e) => {
    e.preventDefault();
    if (confirmPasswordLoading || !confirmAction) return;
    if (!confirmPassword) {
      setConfirmPasswordError(t("password.currentRequired"));
      return;
    }

    setConfirmPasswordLoading(true);
    setConfirmPasswordError('');

    try {
      const csrfToken = await getCsrfToken();

      if (confirmAction === 'api-key') {
        setApiKeyCreating(true);
        setApiKeyVisible(false);
        setApiKeyCopyDone(false);
        try {
          const res = await fetch(getApiUrl('/api/internal/api-key/create/'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
            body: JSON.stringify({ current_password: confirmPassword }),
          });
          const data = await res.json().catch(() => ({}));
          if (!data.success || !data.api_key) {
            setConfirmPasswordError(
              data.detail || data.message || t("apiKey.resetFailed")
            );
            return;
          }
          setApiKey(data.api_key);
          setHasApiKey(true);
          clearConfirmPasswordModal();
        } finally {
          setApiKeyCreating(false);
        }
        return;
      }

      if (confirmAction === 'reveal-api-key') {
        const res = await fetch(getApiUrl('/api/internal/api-key/read/'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
          body: JSON.stringify({ current_password: confirmPassword }),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success || !data.api_key) {
          setConfirmPasswordError(
            data.detail || data.message || t("apiKey.readFailed")
          );
          return;
        }
        setApiKey(data.api_key);
        setHasApiKey(true);
        if (pendingRevealIntent === 'copy') {
          try {
            await navigator.clipboard.writeText(data.api_key);
            setApiKeyCopyDone(true);
            setTimeout(() => setApiKeyCopyDone(false), 2000);
          } catch (_) {
            setApiKeyVisible(true);
          }
        } else {
          setApiKeyVisible(true);
        }
        clearConfirmPasswordModal();
        return;
      }

      if (confirmAction === '2fa') {
        setTwoFaSaving(true);
        try {
          const { ok, data } = await postSettings({
            is_2fa_enabled: pendingTwoFaEnabled === true,
            current_password: confirmPassword,
          });
          if (!ok || !data.success) {
            setConfirmPasswordError(
              data.message || data.detail || t("twoFactor.updateFailed")
            );
            return;
          }
          setTwoFaEnabled(pendingTwoFaEnabled === true);
          clearConfirmPasswordModal();
        } finally {
          setTwoFaSaving(false);
        }
      }
    } catch (err) {
      console.error('Confirm password action failed:', err);
      setConfirmPasswordError(t("errors.network"));
    } finally {
      setConfirmPasswordLoading(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!hasApiKey) return;
    if (!apiKey) {
      openConfirmPasswordModal('reveal-api-key', 'copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(apiKey);
      setApiKeyCopyDone(true);
      setTimeout(() => setApiKeyCopyDone(false), 2000);
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  const handleToggleApiKeyVisibility = () => {
    if (!hasApiKey) return;
    if (apiKeyVisible) {
      setApiKeyVisible(false);
      return;
    }
    if (!apiKey) {
      openConfirmPasswordModal('reveal-api-key', 'view');
      return;
    }
    setApiKeyVisible(true);
  };

  const handleMaxFailedTestsChange = (e) => {
    const next = e.target.value.replace(/[^\d]/g, '');
    setMaxFailedTests(next);
    setMaxFailedTestsMessage('');
    setMaxFailedTestsError('');
  };

  const handleMaxFailedTestsBlur = () => {
    setMaxFailedTests(String(clampMaxFailedTests(maxFailedTests)));
  };

  const handleMaxFailedTestsSave = async () => {
    if (maxFailedTestsSaving) return;

    const clamped = clampMaxFailedTests(maxFailedTests);
    setMaxFailedTests(String(clamped));
    setMaxFailedTestsSaving(true);
    setMaxFailedTestsMessage('');
    setMaxFailedTestsError('');

    try {
      const { ok, data } = await postSettings({ maximum_failed_tests: clamped });

      if (ok && data.success) {
        setMaxFailedTestsMessage(t("maxFailedTests.saved"));
        setTimeout(() => setMaxFailedTestsMessage(''), 2000);
      } else {
        setMaxFailedTestsError(data.message || t("maxFailedTests.saveFailed"));
      }
    } catch (err) {
      setMaxFailedTestsError(t("maxFailedTests.saveFailed"));
    } finally {
      setMaxFailedTestsSaving(false);
    }
  };

  const handleJobsApiTargetChange = (target) => {
    const next = target === "localhost" ? "localhost" : "server";
    setJobsApiTarget(next);
    writeJobsApiTarget(next);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl('/api/internal/auth/change-password/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        setPasswordSuccess(t("password.changedSuccess"));
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordSuccess('');
        }, 1200);
      } else {
        setPasswordError(data.message || t("password.changeFailed"));
      }
    } catch (err) {
      setPasswordError(t("errors.network"));
    } finally {
      setPasswordLoading(false);
    }
  };

  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const canChangeLanguage = (userData?.email || "").toLowerCase().endsWith("@ironsdn.com");
  const apiKeyBusy = apiKeyCreating || apiKeyLoading;
  const apiKeyMissing = apiKeyLoading || !hasApiKey;
  const twoFaBusy = twoFaLoading || twoFaSaving;

  return (
    <div>
      <Header />
      <div className="main-container">
        <div className={`${sidebarCollapsed ? 'col-1' : 'col-2'} p-0`}>
          <Sidebar />
        </div>
        <main className={`${sidebarCollapsed ? 'col-11' : 'col-10'}`} style={{ width: "100%", overflowX: "auto" }}>
          <div className="dashboard">
            <div className="profile" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h1>{t("title")}</h1>
            </div>

            {loading ? (
              <div className="panel-default2 padding2">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "30vh"
                  }}
                >
                  <div style={{ fontSize: "18px" }}>{t("loading")}</div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "24px",
                  alignItems: "stretch"
                }}
              >
                {/* Account info */}
                <div className="panel-default2" style={panelStyle}>
                  <div style={panelBodyStyle}>
                    <div style={fieldRowStyle}>
                      <span style={fieldLabelStyle}>{t("fields.userId")}</span>
                      <span
                        style={{
                          ...valuePillStyle,
                          fontFamily: "monospace",
                          maxWidth: "70%",
                          textAlign: "right",
                          wordBreak: "break-all"
                        }}
                      >
                        {userData?.id || t("notAvailable")}
                      </span>
                    </div>

                    <div style={fieldRowStyle}>
                      <span style={fieldLabelStyle}>{t("fields.email")}</span>
                      <span
                        style={{
                          ...valuePillStyle,
                          maxWidth: "70%",
                          textAlign: "right",
                          wordBreak: "break-all"
                        }}
                      >
                        {userData?.email || t("notAvailable")}
                      </span>
                    </div>

                    <div style={fieldRowStyle}>
                      <span style={fieldLabelStyle}>{t("fields.password")}</span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          height: "32px"
                        }}
                      >
                        <span
                          style={{
                            ...valuePillStyle,
                            fontFamily: "monospace",
                            paddingLeft: "10px",
                            paddingRight: "10px",
                            flexShrink: 0
                          }}
                          aria-label={t("password.hidden")}
                        >
                          <span
                            style={{
                              letterSpacing: "0.2em",
                              // Cancel trailing letter-spacing so L/R padding match
                              marginRight: "-0.2em"
                            }}
                          >
                            ••••••
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={openPasswordModal}
                          aria-label={t("password.change")}
                          title={t("password.change")}
                          style={iconBtnStyle}
                        >
                          <Pencil size={16} aria-hidden />
                        </button>
                      </div>
                    </div>

                    {canChangeLanguage && (
                      <div style={fieldRowStyle}>
                        <span style={fieldLabelStyle}>{t("fields.language")}</span>
                        <select
                          id="account-language"
                          value={language}
                          onChange={(event) => i18n.changeLanguage(event.target.value)}
                          style={languageSelectStyle}
                        >
                          <option value="en">English</option>
                          <option value="fr">Français</option>
                          <option value="es">Español</option>
                        </select>
                      </div>
                    )}

                    <div style={fieldRowStyle}>
                      <span style={fieldLabelStyle}>{t("fields.runTestsFrom")}</span>
                      <select
                        id="account-jobs-api-target"
                        value={jobsApiTarget === "localhost" ? "localhost" : "server"}
                        onChange={(event) => handleJobsApiTargetChange(event.target.value)}
                        aria-label={t("fields.runTestsFrom")}
                        style={languageSelectStyle}
                      >
                        <option value="server">{t("jobsApiTarget.server")}</option>
                        <option value="localhost">{t("jobsApiTarget.localhost")}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* API key */}
                <div className="panel-default2" style={panelStyle}>
                  <div style={panelBodyStyle}>
                    <div style={fieldRowStyle}>
                      <span style={{ ...fieldLabelStyle, minWidth: 0 }}>{t("fields.apiKey")}</span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          height: "32px",
                          flexShrink: 0
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => openConfirmPasswordModal('api-key')}
                          disabled={apiKeyBusy}
                          aria-label={apiKeyCreating ? t("apiKey.resetting") : t("apiKey.reset")}
                          title={apiKeyCreating ? t("apiKey.resettingEllipsis") : t("apiKey.reset")}
                          style={{
                            ...iconBtnStyle,
                            opacity: apiKeyBusy ? 0.7 : 1,
                            cursor: apiKeyBusy ? "default" : "pointer"
                          }}
                        >
                          {apiKeyCreating ? (
                            <Loader2 className="account-api-key-icon-spin" size={16} aria-hidden />
                          ) : (
                            <RefreshCw size={16} aria-hidden />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={handleCopyApiKey}
                          disabled={apiKeyMissing}
                          aria-label={apiKeyCopyDone ? t("copied") : t("apiKey.copy")}
                          title={apiKeyCopyDone ? t("copied") : t("copy")}
                          style={{
                            ...iconBtnStyle,
                            opacity: apiKeyMissing ? 0.5 : 1,
                            cursor: apiKeyMissing ? "default" : "pointer"
                          }}
                        >
                          {apiKeyCopyDone ? (
                            <Check size={16} aria-hidden />
                          ) : (
                            <Copy size={16} aria-hidden />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={handleToggleApiKeyVisibility}
                          disabled={apiKeyMissing}
                          aria-label={apiKeyVisible ? t("apiKey.hide") : t("apiKey.view")}
                          title={apiKeyVisible ? t("hide") : t("view")}
                          style={{
                            ...iconBtnStyle,
                            opacity: apiKeyMissing ? 0.5 : 1,
                            cursor: apiKeyMissing ? "default" : "pointer"
                          }}
                        >
                          {apiKeyVisible ? (
                            <EyeOff size={16} aria-hidden />
                          ) : (
                            <Eye size={16} aria-hidden />
                          )}
                        </button>
                      </div>
                    </div>
                    <span
                      style={{
                        ...valuePillStyle,
                        display: "flex",
                        justifyContent: "flex-start",
                        fontFamily: "monospace",
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        letterSpacing: apiKeyVisible && apiKey ? undefined : "0.12em"
                      }}
                      title={apiKeyVisible && apiKey ? apiKey : undefined}
                      aria-label={apiKeyVisible && apiKey ? t("apiKey.value") : t("apiKey.hidden")}
                    >
                      {apiKeyLoading
                        ? t("loadingEllipsis")
                        : !hasApiKey
                          ? t("notAvailable")
                          : apiKeyVisible && apiKey
                            ? apiKey
                            : '••••••••••••••••••••••••••••••••'}
                    </span>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "var(--muted-text)",
                        margin: 0,
                        opacity: 0.9,
                        lineHeight: 1.45
                      }}
                    >
                      {t("apiKey.description")}
                    </p>
                  </div>
                </div>

                {/* 2FA */}
                <div className="panel-default2" style={panelStyle}>
                  <div style={panelBodyStyle}>
                    <div style={fieldRowStyle}>
                      <span style={{ ...fieldLabelStyle, minWidth: 0, flex: "1 1 auto" }}>
                        {t("fields.twoFactor")}
                      </span>
                      <button
                        type="button"
                        onClick={() => openConfirmPasswordModal('2fa')}
                        disabled={twoFaBusy}
                        style={{
                          position: "relative",
                          width: "44px",
                          height: "24px",
                          borderRadius: "999px",
                          border: "none",
                          padding: 0,
                          backgroundColor: twoFaEnabled ? "#22c55e" : "#9ca3af",
                          cursor: twoFaBusy ? "default" : "pointer",
                          opacity: twoFaBusy ? 0.7 : 1,
                          transition: "background-color 0.18s ease-out, box-shadow 0.18s ease-out",
                          boxShadow: "0 0 0 1px rgba(148,163,184,0.45), 0 2px 5px rgba(15,23,42,0.45)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          overflow: "hidden",
                          flexShrink: 0
                        }}
                      >
                        <span
                          style={{
                            position: "relative",
                            display: "block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                            boxShadow: "0 1px 4px rgba(15,23,42,0.4)",
                            transform: twoFaEnabled ? "translateX(20px)" : "translateX(2px)",
                            transition: "transform 0.18s ease-out"
                          }}
                        />
                      </button>
                    </div>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "var(--muted-text)",
                        margin: 0,
                        opacity: 0.9,
                        lineHeight: 1.45
                      }}
                    >
                      {twoFaLoading
                        ? t("twoFactor.checking")
                        : t("twoFactor.description")}
                    </p>
                  </div>
                </div>

                {/* Maximum failed tests */}
                <div className="panel-default2" style={panelStyle}>
                  <div style={panelBodyStyle}>
                    <div style={fieldRowStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: 0,
                          flex: "1 1 auto"
                        }}
                      >
                        <span style={{ ...fieldLabelStyle, minWidth: 0 }}>
                          {t("fields.maxFailedTests")}
                        </span>
                        {maxFailedTestsMessage && (
                          <span style={{ fontSize: "12px", color: "#22c55e", fontWeight: 500 }}>
                            {maxFailedTestsMessage}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexShrink: 0
                        }}
                      >
                        <input
                          id="account-max-failed-tests"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={maxFailedTests}
                          onChange={handleMaxFailedTestsChange}
                          onBlur={handleMaxFailedTestsBlur}
                          disabled={maxFailedTestsSaving}
                          aria-label={t("fields.maxFailedTests")}
                          style={{
                            width: "110px",
                            height: "32px",
                            padding: "0 10px",
                            border: "2px solid var(--card-border)",
                            borderRadius: "8px",
                            backgroundColor: "var(--card-bg)",
                            color: "var(--card-text)",
                            fontSize: "13px",
                            fontFamily: "monospace",
                            textAlign: "right",
                            boxSizing: "border-box"
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleMaxFailedTestsSave}
                          disabled={maxFailedTestsSaving}
                          className="auth-button"
                          style={{
                            marginTop: 0,
                            height: "32px",
                            padding: "0 14px",
                            fontSize: "13px",
                            borderRadius: "8px",
                            opacity: maxFailedTestsSaving ? 0.7 : 1
                          }}
                        >
                          {maxFailedTestsSaving ? t("savingEllipsis") : t("save")}
                        </button>
                      </div>
                    </div>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "var(--muted-text)",
                        margin: 0,
                        opacity: 0.9,
                        lineHeight: 1.45
                      }}
                    >
                      {t("maxFailedTests.description")}
                    </p>
                    {maxFailedTestsError && (
                      <div style={{ fontSize: "12px", color: "#ff4d4f" }}>
                        {maxFailedTestsError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showPasswordModal && (
              <div
                className="modal-backdrop"
                style={{ zIndex: 9999 }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) closePasswordModal();
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    maxWidth: "440px",
                    width: "100%",
                    padding: "24px"
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={closePasswordModal}
                    disabled={passwordLoading}
                    aria-label={t("close")}
                    style={{
                      position: "absolute",
                      top: "14px",
                      right: "14px",
                      background: "transparent",
                      border: "none",
                      cursor: passwordLoading ? "default" : "pointer",
                      color: "var(--muted-text)",
                      padding: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: passwordLoading ? 0.5 : 1
                    }}
                  >
                    <X size={18} aria-hidden />
                  </button>

                  <h3 style={{ marginTop: 0, marginBottom: "16px", paddingRight: "28px" }}>
                    {t("password.modalTitle")}
                  </h3>

                  <form onSubmit={handlePasswordSubmit}>
                    {passwordError && (
                      <div className="error-message" style={{ marginBottom: "16px" }}>
                        {passwordError}
                      </div>
                    )}
                    {passwordSuccess && (
                      <div className="success-message" style={{ marginBottom: "16px" }}>
                        {passwordSuccess}
                      </div>
                    )}

                    <div className="form-group" style={{ marginBottom: "12px" }}>
                      <label htmlFor="currentPassword" style={modalLabelStyle}>
                        {t("password.current")}
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        disabled={passwordLoading}
                        autoComplete="current-password"
                        style={modalInputStyle}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: "12px" }}>
                      <label htmlFor="newPassword" style={modalLabelStyle}>
                        {t("password.new")}
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        required
                        disabled={passwordLoading}
                        autoComplete="new-password"
                        style={modalInputStyle}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: "14px" }}>
                      <label htmlFor="confirmPassword" style={modalLabelStyle}>
                        {t("password.confirmNew")}
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        disabled={passwordLoading}
                        autoComplete="new-password"
                        style={modalInputStyle}
                      />
                    </div>

                    <button
                      type="submit"
                      className="auth-button"
                      disabled={passwordLoading}
                      style={{ marginTop: "4px" }}
                    >
                      {passwordLoading ? t("saving") : t("save")}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {confirmAction && (
              <div
                className="modal-backdrop"
                style={{ zIndex: 9999 }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) closeConfirmPasswordModal();
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    maxWidth: "440px",
                    width: "100%",
                    padding: "24px"
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={closeConfirmPasswordModal}
                    disabled={confirmPasswordLoading}
                    aria-label={t("close")}
                    style={{
                      position: "absolute",
                      top: "14px",
                      right: "14px",
                      background: "transparent",
                      border: "none",
                      cursor: confirmPasswordLoading ? "default" : "pointer",
                      color: "var(--muted-text)",
                      padding: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: confirmPasswordLoading ? 0.5 : 1
                    }}
                  >
                    <X size={18} aria-hidden />
                  </button>

                  <h3 style={{ marginTop: 0, marginBottom: "8px", paddingRight: "28px" }}>
                    {confirmModalCopy.title}
                  </h3>
                  <p
                    style={{
                      margin: "0 0 16px",
                      fontSize: "14px",
                      color: "var(--muted-text)",
                      lineHeight: 1.45
                    }}
                  >
                    {confirmModalCopy.description}
                  </p>

                  <form onSubmit={handleConfirmPasswordSubmit}>
                    {confirmPasswordError && (
                      <div className="error-message" style={{ marginBottom: "16px" }}>
                        {confirmPasswordError}
                      </div>
                    )}

                    <div className="form-group" style={{ marginBottom: "14px" }}>
                      <label htmlFor="confirmActionPassword" style={modalLabelStyle}>
                        {t("password.current")}
                      </label>
                      <input
                        type="password"
                        id="confirmActionPassword"
                        name="confirmActionPassword"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setConfirmPasswordError('');
                        }}
                        required
                        disabled={confirmPasswordLoading}
                        autoComplete="current-password"
                        autoFocus
                        style={modalInputStyle}
                      />
                    </div>

                    <button
                      type="submit"
                      className="auth-button"
                      disabled={confirmPasswordLoading}
                      style={{ marginTop: "4px" }}
                    >
                      {confirmPasswordLoading
                        ? confirmModalCopy.submittingLabel
                        : confirmModalCopy.submitLabel}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Account;
